import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loading } from '~/components/composites/loading'
import { RequireRouteAccess } from '~/components/composites/require-route-access'
import { Header } from '~/components/header'
import { AppSidebar } from '~/components/sidebar'
import { SidebarInset, SidebarProvider } from '~/components/ui/sidebar'
import { useProfile } from '~/hooks/use-profile'
import { useAuthStore } from '~/stores/auth'
import { useSessionStore } from '~/stores/session'
import { useSettingsStore } from '~/stores/settings'

const AUTH_PATHNAMES = ['/login']

type PortalLayoutProps = {
  children: ReactNode
}

export function PortalLayout({ children }: PortalLayoutProps) {
  const location = useLocation()
  const accessToken = useAuthStore((state) => state.accessToken)
  const user = useSessionStore((state) => state.user)
  const openSidebar = useSettingsStore((state) => state.openSidebar)
  const setOpenSidebar = useSettingsStore((state) => state.setOpenSidebar)
  const isAuthPath = AUTH_PATHNAMES.includes(location.pathname)

  const { isLoading } = useProfile()

  if (!accessToken && !isAuthPath) {
    return <Navigate to="/login" replace />
  }

  if (isAuthPath && accessToken) {
    return <Navigate to="/" replace />
  }

  if (isAuthPath) {
    return <>{children}</>
  }

  if (isLoading && !user) {
    return <Loading />
  }

  return (
    <SidebarProvider open={openSidebar} onOpenChange={setOpenSidebar}>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="flex-1 p-4">
          <RequireRouteAccess>{children}</RequireRouteAccess>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
